import React from 'react';
import { Flex, Tag, Typography, theme } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';

import type { CheckRow } from '../diagnosticsReportUtils';
import { blockedCrawlerTagStyle } from './blockedCrawlerTagStyle';

type DiagnosticsSummaryRowProps = {
  domain: string;
  url: string;
  blockedProviders: string[];
  checkRows: CheckRow[];
  crawlersTested: string[];
  print?: boolean;
};

export default function DiagnosticsSummaryRow({
  domain,
  url,
  blockedProviders,
  checkRows,
  crawlersTested,
  print = false,
}: DiagnosticsSummaryRowProps) {
  const { token } = theme.useToken();

  const cellStyle: React.CSSProperties = {
    padding: token.paddingMD,
    verticalAlign: 'top',
  };

  return (
    <tr>
      <td style={cellStyle}>
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Domain
        </Typography.Text>
        <Flex gap={token.marginXXS} align="center" style={{ whiteSpace: 'nowrap' }}>
          <Typography.Text>{domain}</Typography.Text>
          {!print && (
            <CustomTooltip
              placement="top"
              message={`We used ${url}<br />to verify AI bot access to your site.`}
            >
              <InfoCircleOutlined style={{ fontSize: token.fontSize, color: token.colorTextSecondary, cursor: 'pointer' }} />
            </CustomTooltip>
          )}
        </Flex>
        {print && (
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM, wordBreak: 'break-all' }}>
            {url}
          </Typography.Text>
        )}
      </td>
      <td style={cellStyle}>
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Blocked AI Crawlers
        </Typography.Text>
        <div>
          <BlockedCrawlersSummary blockedProviders={blockedProviders} checkRows={checkRows} />
        </div>
      </td>
      <td style={cellStyle}>
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Crawlers tested
        </Typography.Text>
        <div>
          <Typography.Text>{crawlersTested.join(', ')}</Typography.Text>
        </div>
      </td>
    </tr>
  );
}

function BlockedCrawlersSummary({
  blockedProviders,
  checkRows,
}: {
  blockedProviders: string[];
  checkRows: CheckRow[];
}) {
  const { token } = theme.useToken();

  // Precedence block → couldn't-finish → clear (PRE-3158): a confirmed block always
  // wins; otherwise if any check couldn't finish we can't promise the site is open, so
  // "Check failed to run" instead of the reassuring green. blockedProviders is derived
  // from the check rows, which already hide blocks on untrusted (failed) checks.
  if (blockedProviders.length > 0) {
    return (
      <Flex gap={token.marginXS} wrap="wrap">
        {blockedProviders.map((provider) => (
          <Tag key={provider} icon={<CloseCircleOutlined />} style={blockedCrawlerTagStyle}>
            {provider}
          </Tag>
        ))}
      </Flex>
    );
  }

  if (checkRows.some((row) => row.failed)) {
    return <Tag icon={<MinusCircleOutlined />}>Check failed to run</Tag>;
  }

  return (
    <Tag icon={<CheckCircleOutlined />} color="success">
      No blocked crawlers
    </Tag>
  );
}
